import {createCustomAjvValidator} from "../services/AjvValidator";
import {imageFileTypes, nonImageFileTypes, protocolConstants} from "./constants";

/**
 * Validates and returns a protocol value, defaulting to HTTP if invalid.
 * @param {string} protocol - The protocol to validate
 * @returns {string} A valid protocol constant
 */
export function getValidProtocol(protocol) {
  if (protocol === protocolConstants.HTTP || protocol === protocolConstants.HTTPS) {
    return protocol;
  }
  return protocolConstants.HTTP;
}

const UNIT_PER_INCH = { inch: 1, cm: 2.54, mm: 25.4 };

/**
 * Convert a measurement value from one unit of measure to another.
 * Supported units: 'inch', 'cm', 'mm'
 * @param {number} value - The value to convert
 * @param {string} fromUnit - Source unit ('inch' | 'cm' | 'mm')
 * @param {string} toUnit - Target unit ('inch' | 'cm' | 'mm')
 * @param {number} decimals - Decimal places to round to
 * @returns {number}
 */
export function convertUnitValue(value, fromUnit, toUnit, decimals) {
  if (fromUnit === toUnit) return value;
  const converted = value * (UNIT_PER_INCH[toUnit] / UNIT_PER_INCH[fromUnit]);
  return decimals !== undefined ? +converted.toFixed(decimals) : converted;
}

/**
 * Returns true if the string value has more decimal places than allowed.
 * @param {string} rawValue
 * @param {string} uom - 'inch' | 'cm' | 'mm'
 * @param {object} fieldConfig - entry from DECIMAL_LIMITS (e.g. DECIMAL_LIMITS.size)
 * @returns {boolean}
 */
export function exceedsDecimalLimit(rawValue, uom, fieldConfig) {
  if (!rawValue.includes('.')) return false;
  const maxDecimals = fieldConfig[uom];
  const decimalPart = rawValue.split('.')[1];
  return decimalPart.length > maxDecimals;
}

export function setDefaultBackgroundColor(extend) {
    if(!extend.hasOwnProperty('backgroundColor') && !extend.hasOwnProperty('transparentBackground')) {
        extend.backgroundColor = "#ffffff"
    } else if (!extend.hasOwnProperty('backgroundColor') && extend.transparentBackground === false ) {
        extend.backgroundColor = "#ffffff"
    }
    return extend
}

export function calculateAllEyeBallRotationForEachEyeBall(eyeBallsRotatedAngle) {
  let ball1Rotation = eyeBallsRotatedAngle > 180 ? (eyeBallsRotatedAngle - 360) : eyeBallsRotatedAngle;
  let ball2Rotation = eyeBallsRotatedAngle < 180 ? (-eyeBallsRotatedAngle - 90) : (270 - eyeBallsRotatedAngle);
  if(Math.abs(ball2Rotation) > 180 ){
    ball2Rotation = 360 - Math.abs(ball2Rotation)
  }
  let ball3Rotation = eyeBallsRotatedAngle > 180 ? (-270 - ball1Rotation) : (90 - eyeBallsRotatedAngle);
  if(Math.abs(ball3Rotation) > 180 ){
    ball3Rotation = 360 - Math.abs(ball3Rotation)
  }
  return [ball1Rotation, ball2Rotation, ball3Rotation]
}

export function setEyesAndModules(extend) {
    console.log("setEyesAndModules", JSON.stringify(extend))
    if(!extend.hasOwnProperty('changeIndividualEyes') || extend.changeIndividualEyes === false) {
        const eyeLidAngle = extend.allEyeLidsRotation
        if(eyeLidAngle === 90) {
            extend.eye1Rotation = ["fv"]
            extend.eye1RotationAngle = 90
            extend.eye2Rotation = ["fh","fv"]
            extend.eye2RotationAngle = 90
            extend.eye3Rotation = []
            extend.eye3RotationAngle = 90
        } else if(eyeLidAngle === 180) {
            extend.eye1Rotation = ["fh","fv"]
            extend.eye1RotationAngle = 180
            extend.eye2Rotation = ["fv"]
            extend.eye2RotationAngle = 180
            extend.eye3Rotation = ["fh"]
            extend.eye3RotationAngle = 180
        } else if(eyeLidAngle === 270) {
            extend.eye1Rotation = ["fh"]
            extend.eye1RotationAngle = 270
            extend.eye2Rotation = []
            extend.eye2RotationAngle = 270
            extend.eye3Rotation = ["fh", "fv"]
            extend.eye3RotationAngle = 270
        } else {
            extend.eye1Rotation = []
            extend.eye1RotationAngle = 0
            extend.eye2Rotation = ["fh"]
            extend.eye2RotationAngle = 0
            extend.eye3Rotation = ["fv"]
            extend.eye3RotationAngle = 0
        }
        if(extend.hasOwnProperty('allEyeBallsRotation') ) {

            let eyeBallsRotatedAngle = extend.allEyeBallsRotation
            let ballRotations = calculateAllEyeBallRotationForEachEyeBall(eyeBallsRotatedAngle)
            extend.ball1Rotation = [ballRotations[0]]
            extend.ball2Rotation = [ballRotations[1]]
            extend.ball3Rotation = [ballRotations[2]]
            extend.ball1RotationAngle = ballRotations[0]
            extend.ball2RotationAngle = ballRotations[1]
            extend.ball3RotationAngle = ballRotations[2]
        }

    } else if(extend.hasOwnProperty('changeIndividualEyes') && extend.changeIndividualEyes === true) {
        extend.allEyeLidsRotation = 0
        extend.allEyeBallsRotation = 0
    }
    return extend
}
/**
 * Core URL validation logic (returns validation result object)
 * Shared by urlValidator (throws) and AJV format validator (returns boolean)
 * @param {string} url - The URL to validate
 * @returns {{valid: boolean, error?: string}} - Validation result
 */
export function UrlValidator(url) {
  if (!url) {
    return { valid: false, error: "URL is required" };
  }

  try {
    const urlObj = new URL(url);

    // Block dangerous and unsupported protocols
    const blockedProtocols = ['javascript:', 'data:', 'file:', 'vbscript:', 'ftp:', 'ftps:'];
    if (blockedProtocols.includes(urlObj.protocol)) {
      return { valid: false, error: "Blocked protocol for security reasons" };
    }

    // For http/https, validate hostname strictly (must be a proper domain)
    if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
      if (!urlObj.hostname) {
        return { valid: false, error: "Missing hostname" };
      }

      const hostname = urlObj.hostname;

      if (!hostname.includes('.')) {
        return { valid: false, error: "URL is invalid" };
      }

      if (hostname.startsWith('.') || hostname.endsWith('.')) {
        return { valid: false, error: "URL cannot start or end with a dot" };
      }

      const parts = hostname.split('.');
      if (parts.some(part => part.length === 0)) {
        return { valid: false, error: "Hostname has invalid structure" };
      }
    }

    // For other protocols (deeplinks), just validate parseability
    return { valid: true };
  } catch (error) {
    if (error instanceof TypeError) {
      return { valid: false, error: "Invalid URL format" };
    }
    return { valid: false, error: error.message };
  }
}
/**
 * Strips the protocol (http:// or https://) from a URL string
 * @param {string} url - The URL string to strip protocol from
 * @returns {string} The URL without the protocol prefix
 */
export function stripProtocol(url) {
  if (!url) return url;
  return url.replace(/^https?:\/\//i, '');
}

/**
 * Validates URLs using WHATWG URL Standard (throws on invalid)
 * Supports HTTP/HTTPS URLs and custom deeplinks (tel:, mailto:, upsmobile:, etc.)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid
 * @throws {Error} - If URL is invalid
 */
export function urlValidator(url) {
  const result = UrlValidator(url);
  if (!result.valid) {
    throw new Error(result.error);
  }
  return true;
}
export function  addHttpsIfNeeded(url) {
  if (!url) return url;

  // Check for :// pattern (http://, https://, custom://, etc.)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(url)) {
    return url;
  }

  // Check for non-authority schemes (tel:, mailto:, sms:, etc.)
  const nonAuthoritySchemes = ['tel', 'mailto', 'sms', 'geo', 'urn', 'news', 'data'];
  const schemeMatch = url.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):(.+)/);
  if (schemeMatch && nonAuthoritySchemes.includes(schemeMatch[1].toLowerCase())) {
    return url;
  }

  // No protocol found (or just host:port), add http://
  return 'http://' + url;
}
export function validateAndFormatUrl({url, targetObj, key,}) {
  if (!url) return;
  targetObj[key] = addHttpsIfNeeded(url);
    urlValidator(targetObj[key]);
}


export const getJSONFromBase64 = (base64String, defaultVal = {}) => {
  if (!base64String) return defaultVal;

  try {
    const binary = atob(base64String);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch (err) {
    console.error(err);
    return defaultVal;
  }
};

export const getBase64String = (value) => {
  try {
    const json = JSON.stringify(value);
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  } catch (err) {
    console.error(err);
    return null;
  }
};

export function isValidGTIN(gtin) {
  if (!/^[0-9]{14}$/.test(gtin)) return false;

  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const digit = parseInt(gtin[i]);
    sum += i % 2 === 0 ? digit * 3 : digit;
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(gtin[13]);
}


export const isJSONSchemaValid = (schema, data) => {
  try {
    const ajv = createCustomAjvValidator().ajv;
    const validate = ajv.compile(schema);
    const valid = validate(data);
    if (!valid) {
      console.error(`Error: Failed to validate form data`, validate.errors);
    }
    return valid;
  } catch (err) {
    console.error(err);
  }
  return false;
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const hasNoSpaces = (str) => {
  return !str.includes(' ');
}


export function getRuleTypes(rules) { // get the list of rules types
  const ruleTypes = [];

  function extractTypes(rule) {
    if (rule.type) {
      ruleTypes.push(rule.type);
    }

    if (Array.isArray(rule.rules)) {
      rule.rules.forEach(extractTypes);
    }
  }

  rules.forEach(extractTypes);
  return ruleTypes;
}

/**
 * Convert standard UNIX cron expression to AWS cron expression
 * https://docs.aws.amazon.com/scheduler/latest/UserGuide/schedule-types.html#cron-based
 *
 *  | Field        | Values          | Wildcards     |
 *  | :----------: | :-------------: | :-----------: |
 *  | Minute       | 0-59            | , - * /       |
 *  | Hour         | 0-23            | , - * /       |
 *  | Day-of-month | 1-31            | , - * ? / L W |
 *  | Month        | 1-12 or JAN-DEC | , - * /       |
 *  | Day-of-week  | 1-7 or SUN-SAT  | , - * ? L #   |
 *  | Year         | 1970-2199       | , - * /       |
 */
export class CronUtils {
  static convertUnixToAWS(unixCron) {
    let parts = unixCron.split(" ");
    if (parts.length !== 5) {
      console.error("Invalid Unix cron expression")
      return '';
    };

    let [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Day-of-week
    if (dayOfWeek !== "*") {
      dayOfWeek = dayOfWeek.split(",").map(segment => {
        if (segment.includes("-")) {
          // Handle ranges wildcard '-'
          let [start, end] = segment.split("-").map(Number);
          return `${start + 1}-${end + 1}`;
        } else if (segment.includes("/")) {
          // Handle '/' wildcard to AWS equivalent '#'
          let [start, step] = segment.split("/").map(Number);
          return `${start + 1}#${step}`;
        }
        return (parseInt(segment) + 1).toString();
      }).join(",");
      dayOfMonth = "?";
    } else {
      dayOfWeek = "?";
    }

    return `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek} *`;
  }

  static convertAwsToUnix(awsCron) {
    let parts = awsCron.split(" ");
    if (parts.length !== 6) {
      console.error("Invalid AWS cron expression");
      return '';
    }

    let [minute, hour, dayOfMonth, month, dayOfWeek, year] = parts;

    // Day-of-week
    if (dayOfWeek !== "?") {
      dayOfWeek = dayOfWeek.split(",").map(segment => {
        if (segment.includes("-")) {
          // Handle ranges wildcard '-'
          let [start, end] = segment.split("-").map(Number);
          return `${start - 1}-${end - 1}`;
        } else if (segment.includes("#")) {
          // Handle '/' wildcard to AWS equivalent '#'
          let [start, step] = segment.split("#").map(Number);
          return `${start - 1}/${step}`;
        }
        return (parseInt(segment) - 1).toString();
      }).join(",");
      dayOfMonth = "*";
    } else {
      dayOfWeek = "*";
    }

    return `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
  }
}

export function addTimestampToFileName(fileName) {
  const timestamp = new Date().getTime();
  const dotIndex = fileName.lastIndexOf('.');
  const fileNameWithoutType = dotIndex === -1 ? fileName : fileName.slice(0, dotIndex);
  const fileTypeFromName = dotIndex === -1 ? '' : fileName.slice(dotIndex);
  return `${fileNameWithoutType}_${timestamp}${fileTypeFromName}`;
}

export function getFileFormat(file) {
  if (isEpsFormat(file)) return "eps";
  return file?.type.split("/")[1]?.toLowerCase() === "mpeg"
    ? file?.type.includes("video")
      ? "mp4"
      : "mp3"
    : file?.type.split("/")[1]?.toLowerCase() === "quicktime"
      ? "mov"
      : file?.type.split("/")[1]?.toLowerCase();
}

export function supportedImageFileTypes(type) {
  return [imageFileTypes.PNG, imageFileTypes.JPEG, imageFileTypes.GIF, ...imageFileTypes.SVG,...imageFileTypes.EPS, ...imageFileTypes.TIFF, imageFileTypes.BMP].includes(type)
}

export function supportedNonImageFileTypes(type) {
  return [nonImageFileTypes.MP3, nonImageFileTypes.MP4, nonImageFileTypes.WAV, nonImageFileTypes.MOV, nonImageFileTypes.PDF, nonImageFileTypes.HTML].includes(type);
  //, nonImageFileTypes.XML, nonImageFileTypes.DOC, nonImageFileTypes.DOCX, nonImageFileTypes.XLS, nonImageFileTypes.XLSX,
  // nonImageFileTypes.CSV, nonImageFileTypes.TXT,, nonImageFileTypes.PPT, nonImageFileTypes.PPTX,, nonImageFileTypes.JSON,
}

export function getFileCategory(fileExtension) {
  const fileType = fileExtension?.toLowerCase();
  const fileCategories = {
    video: ["mp4", "mov", "avi", "mkv", "webm"],
    audio: ["mp3", "wav", "flac", "aac", "ogg"],
    image: Object.values(imageFileTypes).flat(),
    pdf: ["pdf"],
    html: ["html"],
    // files:['csv', 'zip', 'xls', 'json']
  }
  for (const [category, extensions] of Object.entries(fileCategories)) {
    if (extensions.includes(fileType)) {
      return category;
    }
  }
  return "unknown"; // Return 'unknown' if file type doesn't match any category
}
export function getFileGroup(fileFormat) {
  //group is used in the edit route when a user wants to change a file content they are limited to that group
  const imageExtensions = ['jpg','jpeg', 'png', 'gif', 'svg', 'tif', 'ico', 'dvu'];
  const videoExtensions = [ 'mp4', 'mov', 'wmv', 'avi', 'flv'];
  const audioExtensions = ['mp3', 'wav', 'ogg'];
  const fileExtensions = ['csv', 'zip', 'pdf', 'xls', 'json', 'html'];

  if (imageExtensions.includes(fileFormat)) {
    return "images";
  } else if (videoExtensions.includes(fileFormat)) {
    return "videos";
  } else if (audioExtensions.includes(fileFormat)) {
    return "audios";
  } else if (fileExtensions.includes(fileFormat)) {
    return "files";
  }
  return null;
}
export function getFileExtn(fileTypeOrName) {
  if (!fileTypeOrName) return '';

  // Handle MIME types like "image/png"
  if (fileTypeOrName.includes('/')) {
    return fileTypeOrName.split('/')[1];
  }

  // Handle file names like "photo.jpg" or "archive.tar.gz"
  const match = fileTypeOrName.match(/\.([^.\\\/]+)$/);
  return match ? match[1] : '';
}

export function resolveDownloadFilename(name, cdnUrl) {
  const base = name || 'download';
  if (getFileExtn(base)) return base;
  const urlFilename = (cdnUrl?.split('?')[0] || '').split('/').pop();
  const ext = getFileExtn(urlFilename);
  return ext ? `${base}.${ext}` : base;
}

export function isEpsFormat(fileOrString) {
  if (!fileOrString) return false;

  const EPS_IDENTIFIERS = [
    "eps",
    "postscript",
    "application/postscript",
    "application/eps",
    "image/eps"
  ];

  // Handle string input
  if (typeof fileOrString === "string") {
    const str = fileOrString.toLowerCase();
    return str.endsWith(".eps") || EPS_IDENTIFIERS.includes(str);
  }

  // Handle file object
  const extension = getFileExtn(fileOrString.name || "").toLowerCase();
  const mimeType = fileOrString.type || "";

  return extension === "eps" || EPS_IDENTIFIERS.includes(mimeType);
}
export function createMediaFileUISchema(schema,view) {
  let uiSchema = typeof schema === 'string' ? JSON.parse(schema || '{}') : schema || {};
  Object.keys(uiSchema).forEach(key => {
    if (uiSchema[key] && uiSchema[key]['ui:widget'] === 'mediaFileCodeUploadWidget') {
      uiSchema[key]['ui:options'] = {
        ...uiSchema[key]['ui:options'],
        viewMode: view
      };
    }
  });
  return uiSchema;
}
export const convertByteToMB = (fileInByteSize) => {
  if (typeof fileInByteSize !== 'number' || isNaN(fileInByteSize)) return 0;
  return +(fileInByteSize / 1_000_000).toFixed(1);
};

export function processImageServiceResponse(resp) {
  // New API format - data is already structured with camelCase properties
  let base64Image = resp.data.base64Image;

  return {
    base64Image: base64Image,
    imageFormat: resp.data.imageFormat,
    status: resp.data.status || resp.status,
    moduleWidth: resp.data.moduleWidth,
    qrWidth: resp.data.qrWidth,
    qrWidthPixel: resp.data.qrWidthInPixel || resp.data.qrWidthPixel,
    qrWidthInch: Number(resp.data.qrWidthInInch).toFixed(3),
    qrWidthMM: Number(resp.data.qrWidthInMM).toFixed(3),
    qrVersion: resp.data.qrVersion,
    modulePerSide: resp.data.modulePerSide,
    errorCorrection: resp.data.errorCorrection,
    unit: resp.data.unit,
    quietZone: resp.data.quietZone,
    moduleWidthInPixel: resp.data.moduleWidthInPixel,
    moduleWidthInInch: Number(resp.data.moduleWidthInInch).toFixed(3),
    moduleWidthInMM: Number(resp.data.moduleWidthInMM).toFixed(3),
    bwr: resp.data.bwr
  };
}

export function formatFileName(name) {
  name = name.trim();
  // Change uppercase characters to lowercase
  // name = name.toLowerCase();

  // Replace underscores with dashes
  // name = name.replace(/_/g, '-');

  // Replace slashes with underscores
  name = name.replace(/\//g, '_');

  // Replace spaces with underscores
  name = name.replace(/\s+/g, '_');

  // Remove invalid characters
  // name = name.replace(/[^a-z0-9-_]/g, '');

  // Replace consecutive dashes with a single dash
  name = name.replace(/-{2,}/g, '-');

  return name;
}

/**
 * objA may have the same keys as objB or fewer,
 * but it must NOT contain any key that objB does not have.
 *
 * @param {object} objA     – candidate subset
 * @param {object} objB     – reference superset
 * @returns {boolean}
 */
export function keysSubsetOf(objA, objB) {
  const isObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);

  // if A is an object but B is not, A already has "extra structure"
  if (isObject(objA) && !isObject(objB)) return false;
  // if A is not an object, it adds no new keys → ok
  if (!isObject(objA)) return true;

  // iterate over every own key in A
  return Object.keys(objA).every(key =>
    key in objB &&                        // key must exist in B
    keysSubsetOf(objA[key], objB[key])   // and recurse into children
  );
}

export function extractBuildVersion(version) {
  if(!version) return null;
  if (version === '1.0.0') return version;

  const parts = version.split('.');
  return parts[parts.length - 1];
}

export function getCacheBustedUrl(url) {
  if (!url) return url;
  return url + (url.includes("?") ? "&" : "?") + "cacheBust=" + new Date().getTime();
}

export function getImageType(b64) {
  /* ---------- decode just enough bytes for signature check -------- */
  let bin;
  try {
    if (typeof Buffer !== "undefined") {          // Node
      bin = Buffer.from(b64, "base64");
    } else {                                      // Browser
      bin = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    }
  } catch {
    return null;                                  // invalid base-64
  }

  /* ---------- PDF (%PDF-) --------------------------- */
  if (bin.length >= 5 &&
      bin[0] === 0x25 && bin[1] === 0x50 && bin[2] === 0x44 &&
      bin[3] === 0x46 && bin[4] === 0x2D) {        // "%PDF-"
    return "application/pdf";
  }

  /* ---------- EPS / PostScript ---------------------- */
  //  ASCII EPS:  %!PS-Adobe-3.0 EPSF-...
  //  Binary EPS: C5 D0 D3 C6 (rare, but include)
  if (bin.length >= 4) {
    const a = bin[0], b = bin[1], c = bin[2], d = bin[3];
    if ((a === 0x25 && b === 0x21 && c === 0x50 && d === 0x53) ||      // "%!PS"
        (a === 0xC5 && b === 0xD0 && c === 0xD3 && d === 0xC6)) {      // Binary EPS
      return "application/postscript";
    }
  }

  /* ---------- PNG ----------------------------------- */
  if (bin.length >= 4 &&
      bin[0] === 0x89 && bin[1] === 0x50 &&
      bin[2] === 0x4E && bin[3] === 0x47) {
    return "image/png";
  }

  /* ---------- JPEG ---------------------------------- */
  if (bin.length >= 3 &&
      bin[0] === 0xFF && bin[1] === 0xD8 && bin[2] === 0xFF) {
    return "image/jpeg";
  }

  /* ---------- GIF87a / GIF89a ----------------------- */
  if (bin.length >= 6 &&
      bin[0] === 0x47 && bin[1] === 0x49 && bin[2] === 0x46 &&
      bin[3] === 0x38 &&
      (bin[4] === 0x37 || bin[4] === 0x39) && bin[5] === 0x61) {
    return "image/gif";
  }

  /* ---------- BMP ----------------------------------- */
  if (bin.length >= 2 && bin[0] === 0x42 && bin[1] === 0x4D) {
    return "image/bmp";
  }

  /* ---------- TIFF ---------------------------------- */
  if (bin.length >= 4 &&
      ((bin[0] === 0x49 && bin[1] === 0x49 && bin[2] === 0x2A && bin[3] === 0x00) ||
       (bin[0] === 0x4D && bin[1] === 0x4D && bin[2] === 0x00 && bin[3] === 0x2A))) {
    return "image/tiff";
  }

  /* ---------- not recognised ------------------------ */
  return null;
}

export const formatFeatureName = (key) => {
  return key
    .replace(/_/g, ' ')  // Replace underscores with spaces
    .replace(/\b\w/g, letter => letter.toUpperCase())  // Capitalize first letter of each word
    .replace(/\b(qr|ui|api|url|id|uuid)\b/gi, (match) => match.toUpperCase())  // Uppercase common abbreviations
    .replace(/\bCmp\b/g, 'CMP')  // Fix specific cases
    .replace(/\bGs1\b/g, 'GS1');
};

export const checkForEnabledValues = (obj) => {
  for (let prop in obj) {
    if (prop === 'enabled' && obj[prop] === true) {
      return true;
    }
    if (typeof obj[prop] === 'object' && obj[prop] !== null) {
      if (checkForEnabledValues(obj[prop])) {
        return true;
      }
    }
  }
  return false;
};



/*
  check designer config is valid for using BWR parameter
  it only allows key related to color
*/
export function validForBWRImageConfig(candidate, v1ImageConfig) {
  // Check if we're in dev environment for logging
  const isDev = (process.env["REACT_APP_ENV"] || "local") === "local" ||
                (process.env["REACT_APP_ENV"] || "local") === "development";

  // Helper function for conditional logging
  const devLog = (message, ...args) => {
    if (isDev) console.log(message, ...args);
  };

  const allowedKey = [
    "size",
    "bwr",
    "dataShape",
    "errorCorrection",
    "quietZone",
    "positionKeys",
    "isCMYK",
    "moduleScale",
    "isDefaultDesigner",
    "colorMode",
    "foregroundColor",
    "backgroundModulesColor",
    "foregroundColorUseForeground",
    "imageFormat",
    "backgroundColor",
    "eye1Shape",
    "eye2Shape",
    "eye3Shape",
    "eye1Rotation",
    "eye1RotationAngle",
    "eye2Rotation",
    "eye2RotationAngle",
    "eye3Rotation",
    "eye3RotationAngle",
    "addThreeEyelidColors",
    "backgroundColorUseForeground",
    "transparentBackground",
    "moduleWidth",
    "showImageSizeOrModuleWidth",
    "unitsOfMeasure",
    "barcodeFormat",
    "barcodeSizeUnit",
    "gradientColor1UseForeground",
    "gradientColor2UseForeground",
    "gradientColor1",
    "gradientColor2",
    "gradientType"
  ];
  const defaultPositionKeys = JSON.stringify({
    "Bottom": "bottomText",
    "Left": "leftText",
    "Right": "rightText",
    "Top": "topText"
  });
  const checkList = new Set([
    ...allowedKey,
    ...Object.keys(v1ImageConfig),
  ]);
  
  function walk(node) {
    // primitives introduce no keys → always ok
    if (node === null || typeof node !== 'object' || Array.isArray(node)) {
      return true;
    }

    for (const key of Object.keys(node)) {
      if (!checkList.has(key)) {
        devLog(`Invalid key found: ${key}`);
        return false;            // extra key → fail
      }
      // if (key === 'backgroundColor') {
      //   const bgColor = (node)[key];
      //   const isValidString = bgColor === '#ffffff';
      //   const isValidArray = Array.isArray(bgColor) && 
      //                       bgColor.length === 4 && 
      //                       bgColor[0] === 0 && 
      //                       bgColor[1] === 0 && 
      //                       bgColor[2] === 0 && 
      //                       bgColor[3] === 0;
      //   const isValidCmyk = bgColor === 'cmyk(0%, 0%, 0%, 0%)';
      //   if (!isValidString && !isValidArray && !isValidCmyk) {
      //     devLog("backgroundColor should be #ffffff or [0,0,0,0] or cmyk(0%, 0%, 0%, 0%) for v1 image config");
      //     return false;
      //   }
      // }
      if (key === 'eye1Shape' && (node)[key] !== 0) {
        devLog("eye1Shape should be '0' for v1 image config");
        return false;
      }
      if (key === 'eye2Shape' && (node)[key] !== 0) {
        devLog("eye2Shape should be '0' for v1 image config");
        return false;
      }
      if (key === 'eye3Shape' && (node)[key] !== 0) {
        devLog("eye3Shape should be '0' for v1 image config");
        return false;
      }
      if (key === 'eye1Rotation' && (node)[key].length > 0) {
        devLog("eye1Rotation should be empty for v1 image config");
        return false;
      }
      if (key === 'eye1RotationAngle' && (node)[key] !== 0) {
        devLog("eye1RotationAngle should be 0 for v1 image config");
        return false;
      }
      if (key === 'eye2Rotation' && node[key].length !== 1 && node[key][0] !== 'fh') {
        devLog("eye2Rotation should be ['fh'] for v1 image config", node[key]);
        return false;
      }
      if (key === 'eye2RotationAngle' && (node)[key] !== 0) {
        devLog("eye2RotationAngle should be 0 for v1 image config");
        return false;
      }
      if (key === 'eye3Rotation' && node[key].length !== 1 && node[key][0] !== 'fv') {
        devLog("eye3Rotation should be ['fv'] for v1 image config");
        return false;
      }
      if (key === 'eye3RotationAngle' && (node)[key] !== 0) {
        devLog("eye3RotationAngle should be 0 for v1 image config");
        return false;
      }
      if (key === 'addThreeEyelidColors' && (node)[key] !== false) {
        devLog("addThreeEyelidColors should be false for v1 image config");
        return false;
      }
      if (key === 'backgroundColorUseForeground' && (node)[key] !== false) {
        devLog("backgroundColorUseForeground should be false for v1 image config");
        return false;
      }
      if (key === 'dataShape' && (node)[key] !== 0) {
        devLog("dataShape should be 0 for v1 image config");
        return false;
      }
      if (key === 'positionKeys' && JSON.stringify((node)[key]) !== defaultPositionKeys) {
        devLog("positionKeys should match defaultPositionKeys for v1 image config");
        return false;
      }
      // Skip recursive validation for positionKeys since we already validated its structure
      if (key === 'positionKeys') {
        continue;
      }
      if (key === 'moduleScale' && (node)[key] !== 100) {
        devLog("moduleScale should be 100 for v1 image config");
        return false;
      }
      if (key === 'transparentBackground' && (node)[key] !== false) {
        devLog("transparentBackground should be false for v1 image config");
        return false;
      }
      if (!walk((node)[key])) return false;    // recurse
    }
    return true;
  }

  return walk(candidate);
      
}
